import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import EditorialSubNav from '../components/EditorialSubNav';
import { useT } from '../i18n/LanguageContext';

const eyebrowStyle = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '9pt',
  fontWeight: 600,
  letterSpacing: '0.32em',
  textTransform: 'uppercase',
  color: '#D6A87A',
};

const SectionEyebrow = ({ children }) => (
  <div style={eyebrowStyle} className="mb-6">
    {children}
  </div>
);

const Events = () => {
  const t = useT();
  const subNavLinks = [
    { label: t('subnav.approach'), target: 'approach' },
    { label: t('subnav.method'), target: 'method' },
    { label: t('subnav.scale'), target: 'scale' },
    { label: t('subnav.cases'), target: 'cases' },
    { label: t('subnav.studio'), target: 'studio' },
    { label: t('subnav.contact'), target: 'contact' },
  ];

  const method = ['01', '02', '03', '04', '05'].map((no) => ({
    no,
    title: t(`events.method.${no}.title`),
    desc: t(`events.method.${no}.desc`),
  }));

  const capabilities = [
    { titleKey: 'events.scale.scale', lines: t('events.scale.scaleLines') || [] },
    { titleKey: 'events.scale.scenography', lines: t('events.scale.scenographyLines') || [] },
    { titleKey: 'events.scale.experience', lines: t('events.scale.experienceLines') || [] },
  ];

  const caseMedia = [
    {
      venue: 'LIYA ISLAND',
      image: 'https://images.unsplash.com/photo-1530023367847-a683933f4172?w=1200&q=80',
      fallback: 'https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=1200&q=80',
    },
    {
      venue: 'HILTON BOSPHORUS',
      image: 'https://images.unsplash.com/photo-1712314947761-a8d718bd8c32?w=1200&q=80',
      fallback: 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=1200&q=80',
    },
    {
      venue: 'ÇIRAĞAN PALACE',
      image: 'https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=1200&q=80',
      fallback: 'https://images.unsplash.com/photo-1469371670807-013ccf25f16a?w=1200&q=80',
    },
  ];
  const caseCopy = t('events.cases.items') || [];
  const cases = caseMedia.map((m, i) => ({ ...m, ...(caseCopy[i] || {}) }));

  return (
    <div className="bg-bone text-ink min-h-screen" data-testid="events-page">
      <SEO
        title="Events, Galas, Incentives, Brand Activations | GlowMeets"
        description="Premium event production across Türkiye. From 30-guest private dinners on the Bosphorus to 800-attendee brand activations, designed end-to-end by one studio."
        keywords="event production Turkey, gala dinner Istanbul, brand activation Turkey, incentive dinner Bosphorus, corporate retreat Turkey"
        url="/events"
        service={{
          name: 'GlowMeets Events',
          serviceType: 'Gala dinners, brand activations and milestone events',
          description:
            'Editorial event production across Türkiye. Galas, brand activations, anniversaries and milestone celebrations from 30 to 800 guests.',
        }}
      />

      {/* ───────────────────────── 1. SUB-HERO ───────────────────────── */}
      <section
        className="relative w-full overflow-hidden"
        style={{ minHeight: '88vh' }}
        data-testid="events-subhero"
      >
        <img
          src="https://images.unsplash.com/photo-1712314947761-a8d718bd8c32?w=2200&q=80"
          alt="Banquet hall with chandeliers set for an evening"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to right, rgba(244,239,227,0.94) 0%, rgba(244,239,227,0.86) 38%, rgba(244,239,227,0.5) 65%, rgba(244,239,227,0.18) 100%)',
          }}
        />
        {/* Soft bottom bone fade to transition into the next section */}
        <div
          className="absolute inset-x-0 bottom-0"
          style={{
            height: '180px',
            background:
              'linear-gradient(to bottom, rgba(244,239,227,0) 0%, #F4EFE3 100%)',
          }}
        />

        <div
          className="relative z-10 max-w-container mx-auto px-4 sm:px-6 lg:px-8 flex items-center"
          style={{ minHeight: '88vh', paddingTop: '120px', paddingBottom: '90px' }}
        >
          <div className="max-w-[58ch]">
            <SectionEyebrow>{t('events.hero.eyebrow')}</SectionEyebrow>

            <h1
              className="text-ink"
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: 'clamp(40px, 5vw, 64pt)',
                lineHeight: 1.05,
                fontWeight: 500,
                letterSpacing: '-0.005em',
              }}
            >
              <span className="block">{t('events.hero.line1')}</span>
              <span
                className="block italic"
                style={{ color: '#B98556', fontWeight: 400 }}
              >
                {t('events.hero.line2')}
              </span>
            </h1>

            <p
              className="mt-8"
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontStyle: 'italic',
                fontWeight: 400,
                fontSize: '18pt',
                color: 'rgba(11,15,31,0.75)',
                lineHeight: 1.5,
                maxWidth: '60ch',
              }}
            >
              {t('events.hero.sub')}
            </p>

            <div
              className="mt-10"
              style={{
                height: '1px',
                width: '88px',
                backgroundColor: 'rgba(214,168,122,0.55)',
              }}
            />
          </div>
        </div>
      </section>

      {/* Sticky in-page sub-nav (bone surface) */}
      <EditorialSubNav links={subNavLinks} surface="bone" />

      {/* ───────────────────────── 2. THE APPROACH ───────────────────────── */}
      <section
        id="approach"
        className="bg-bone text-ink"
        style={{ paddingTop: '90px', paddingBottom: '90px' }}
        data-testid="events-approach"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-[60ch]">
            <SectionEyebrow>{t('events.approach.eyebrow')}</SectionEyebrow>
            <h2
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: 'clamp(28px, 2.8vw, 36pt)',
                lineHeight: 1.15,
                fontWeight: 500,
                color: '#0A132F',
                letterSpacing: '-0.005em',
              }}
            >
              {t('events.approach.headline1')}{' '}
              <em
                className="italic"
                style={{ color: '#B98556', fontWeight: 400 }}
              >
                {t('events.approach.headline2')}
              </em>
            </h2>

            <div
              className="mt-10 space-y-6"
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '13pt',
                fontWeight: 400,
                lineHeight: 1.7,
                color: '#4A4F63',
              }}
            >
              {(t('events.approach.body') || []).map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>

            <div
              className="mt-12"
              style={{
                height: '1px',
                width: '64px',
                backgroundColor: 'rgba(214,168,122,0.55)',
              }}
            />
          </div>
        </div>
      </section>

      {/* ───────────────────────── 3. THE METHOD (navy band) ───────────────────────── */}
      <section
        id="method"
        className="bg-navy text-bone"
        style={{ paddingTop: '90px', paddingBottom: '90px' }}
        data-testid="events-method"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 max-w-2xl mx-auto">
            <SectionEyebrow>{t('events.method.eyebrow')}</SectionEyebrow>
            <h2
              className="text-bone"
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: 'clamp(30px, 3vw, 44px)',
                lineHeight: 1.1,
                fontWeight: 500,
              }}
            >
              {t('events.method.headline1')}{' '}
              <em
                className="italic"
                style={{ color: '#D6A87A', fontWeight: 400 }}
              >
                {t('events.method.headline2')}
              </em>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-0">
            {method.map((step, i) => (
              <div
                key={step.no}
                className="relative px-6 py-8"
                style={{
                  borderTop: '1px solid rgba(214,168,122,0.45)',
                  borderRight:
                    i < method.length - 1
                      ? '1px solid rgba(214,168,122,0.18)'
                      : 'none',
                }}
                data-testid={`events-method-${step.no}`}
              >
                <div
                  style={{
                    fontFamily: '"Cormorant Garamond", serif',
                    fontSize: '64pt',
                    lineHeight: 1,
                    fontWeight: 400,
                    fontStyle: 'italic',
                    color: '#D6A87A',
                  }}
                >
                  {step.no}
                </div>

                <div
                  className="mt-6"
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '11pt',
                    fontWeight: 600,
                    letterSpacing: '0.28em',
                    textTransform: 'uppercase',
                    color: '#D6A87A',
                  }}
                >
                  {step.title}
                </div>

                <p
                  className="mt-3"
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '11pt',
                    fontWeight: 400,
                    lineHeight: 1.6,
                    color: 'rgba(244,239,227,0.82)',
                  }}
                >
                  {step.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────────────── 4. SCALE & CAPABILITIES (back to bone) ───────────────────────── */}
      <section
        id="scale"
        className="bg-bone text-ink"
        style={{ paddingTop: '90px', paddingBottom: '90px' }}
        data-testid="events-scale"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <SectionEyebrow>{t('events.scale.eyebrow')}</SectionEyebrow>
            <h2
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontStyle: 'italic',
                fontSize: '28pt',
                lineHeight: 1.1,
                fontWeight: 400,
                color: '#B98556',
                letterSpacing: '-0.005em',
              }}
            >
              {t('events.scale.headline')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-12 gap-y-12">
            {capabilities.map((c, idx) => (
              <div key={c.titleKey} data-testid={`events-capability-${idx + 1}`}>
                <div
                  style={{
                    height: '1px',
                    width: '40px',
                    backgroundColor: 'rgba(214,168,122,0.55)',
                  }}
                  className="mb-5"
                />
                <h3
                  style={{
                    fontFamily: '"Cormorant Garamond", serif',
                    fontSize: '18pt',
                    fontWeight: 500,
                    lineHeight: 1.2,
                    color: '#B98556',
                    letterSpacing: '-0.005em',
                  }}
                  className="mb-5"
                >
                  {t(c.titleKey)}
                </h3>
                <ul className="space-y-3">
                  {c.lines.map((line) => (
                    <li
                      key={line}
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '10pt',
                        fontWeight: 400,
                        lineHeight: 1.6,
                        color: '#4A4F63',
                      }}
                    >
                      {line}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────────────── 5. SIGNATURE EVENINGS ───────────────────────── */}
      <section
        id="cases"
        className="bg-bone text-ink"
        style={{ paddingTop: '0', paddingBottom: '90px' }}
        data-testid="events-cases"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-14 max-w-2xl">
            <SectionEyebrow>{t('events.cases.eyebrow')}</SectionEyebrow>
            <h2
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: 'clamp(30px, 3vw, 44px)',
                lineHeight: 1.1,
                fontWeight: 500,
                color: '#0A132F',
              }}
            >
              {t('events.cases.headline1')}{' '}
              <em
                className="italic"
                style={{ color: '#B98556', fontWeight: 400 }}
              >
                {t('events.cases.headline2')}
              </em>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-12">
            {cases.map((c, index) => (
              <article
                key={c.title}
                className="group"
                data-testid={`events-case-${index + 1}`}
              >
                <div
                  className="relative overflow-hidden"
                  style={{ aspectRatio: '4/5' }}
                >
                  <img
                    src={c.image}
                    alt={c.title}
                    onError={(e) => {
                      if (c.fallback && e.currentTarget.src !== c.fallback) {
                        e.currentTarget.src = c.fallback;
                      }
                    }}
                    className="w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]"
                  />
                  {/* Navy bottom overlay */}
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        'linear-gradient(to bottom, rgba(10,19,47,0) 60%, rgba(10,19,47,0.85) 100%)',
                    }}
                  />
                  {/* Copper category chip */}
                  <div
                    className="absolute top-5 left-5"
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '9pt',
                      fontWeight: 600,
                      letterSpacing: '0.28em',
                      textTransform: 'uppercase',
                      color: '#D6A87A',
                      border: '1px solid rgba(214,168,122,0.6)',
                      padding: '6px 12px',
                      backgroundColor: 'rgba(10,19,47,0.45)',
                    }}
                  >
                    {t('filters.events')}
                  </div>
                </div>

                <div className="mt-6">
                  <div
                    className="mb-3"
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '9pt',
                      fontWeight: 600,
                      letterSpacing: '0.28em',
                      textTransform: 'uppercase',
                      color: '#B98556',
                    }}
                  >
                    {c.kind} · {c.venue}
                  </div>

                  <h3
                    style={{
                      fontFamily: '"Cormorant Garamond", serif',
                      fontSize: '22pt',
                      fontWeight: 500,
                      lineHeight: 1.2,
                      color: '#B98556',
                      letterSpacing: '-0.005em',
                    }}
                  >
                    {c.title}
                  </h3>

                  <p
                    className="mt-3"
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '10pt',
                      fontWeight: 400,
                      lineHeight: 1.65,
                      color: '#4A4F63',
                    }}
                  >
                    {c.summary}
                  </p>

                  <div
                    className="mt-5"
                    style={{
                      height: '1px',
                      width: '32px',
                      backgroundColor: 'rgba(214,168,122,0.55)',
                    }}
                  />

                  <div
                    className="mt-4"
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '9pt',
                      fontWeight: 600,
                      letterSpacing: '0.32em',
                      textTransform: 'uppercase',
                      color: '#B98556',
                    }}
                  >
                    {t('common.outcome')}, {c.outcome}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────────────── 6. THE STUDIO (navy band) ───────────────────────── */}
      <section
        id="studio"
        className="bg-navy text-bone"
        style={{ paddingTop: '90px', paddingBottom: '90px' }}
        data-testid="events-studio"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-[70ch] mx-auto">
            <SectionEyebrow>{t('events.studio.eyebrow')}</SectionEyebrow>

            <p
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontStyle: 'italic',
                fontWeight: 400,
                fontSize: '16pt',
                lineHeight: 1.55,
                color: '#F4EFE3',
                letterSpacing: '-0.002em',
              }}
            >
              {t('events.studio.body')}
            </p>

            <div
              className="mt-10"
              style={{
                height: '1px',
                width: '64px',
                backgroundColor: 'rgba(214,168,122,0.55)',
              }}
            />
          </div>
        </div>
      </section>

      {/* ───────────────────────── 7. SECTION CTA (navy band) ───────────────────────── */}
      <section
        id="contact"
        className="bg-navy text-bone relative overflow-hidden"
        style={{
          paddingTop: '90px',
          paddingBottom: '90px',
          borderTop: '1px solid rgba(214,168,122,0.35)',
        }}
        data-testid="events-cta"
      >
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2"
          style={{
            height: '1px',
            width: '160px',
            backgroundColor: '#D6A87A',
          }}
        />

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <SectionEyebrow>{t('events.cta.eyebrow')}</SectionEyebrow>

          <h2
            className="text-bone"
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontSize: 'clamp(36px, 4vw, 54px)',
              lineHeight: 1.05,
              fontWeight: 500,
            }}
          >
            {t('events.cta.headline1')}{' '}
            <em
              className="italic"
              style={{ color: '#D6A87A', fontWeight: 400 }}
            >
              {t('events.cta.headline2')}
            </em>
          </h2>

          <div className="mt-12 flex flex-col items-center gap-5">
            <Link
              to="/contact"
              data-testid="events-cta-primary"
              className="inline-flex items-center justify-center transition-colors"
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '11pt',
                fontWeight: 600,
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
                color: '#0A132F',
                backgroundColor: '#D6A87A',
                border: '1px solid #D6A87A',
                padding: '14px 32px',
                borderRadius: '999px',
                minWidth: '320px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#B98556';
                e.currentTarget.style.borderColor = '#B98556';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#D6A87A';
                e.currentTarget.style.borderColor = '#D6A87A';
              }}
            >
              {t('cta.bookConsultation')}
            </Link>

            <Link
              to="/events/capabilities"
              data-testid="events-cta-secondary"
              className="inline-flex items-center justify-center transition-colors"
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '11pt',
                fontWeight: 600,
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
                color: '#D6A87A',
                backgroundColor: 'transparent',
                border: '1px solid #D6A87A',
                padding: '14px 32px',
                borderRadius: '999px',
                minWidth: '320px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#D6A87A';
                e.currentTarget.style.color = '#0A132F';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#D6A87A';
              }}
            >
              {t('cta.downloadEventsDeck')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Events;
